import { G } from '@mobily/ts-belt';
import { Typography } from '@mui/material';
import { StepType, TourType } from '@react-admin/ra-tour';
import { useMemo } from 'react';
import { Translate, useTranslate } from 'react-admin';

import { useCanAccess } from '@sh4res/bo-common/hooks';
import { buildLink } from '@sh4res/bo-common/utils/build_link';

import { TranslationKeys } from '@I18n/types';

const lightOverlayOptions = {
  overlayColor: 'rgba(255, 255, 255, 0.65)',
};

const CompanyStep = ({ translate }: { translate: Translate }) => (
  <>
    <Typography
      variant="body1"
      component="span"
      sx={{ fontWeight: '800', marginBottom: '14px', textAlign: 'left', display: 'block' }}>
      {translate('tours.welcome_tour.steps.company.title')}
    </Typography>

    <Typography
      variant="body1"
      component="span"
      sx={{ marginBottom: '14px', textAlign: 'left', display: 'block' }}>
      {translate('tours.welcome_tour.steps.company.content.1')}
      <strong>{translate('tours.welcome_tour.steps.company.content.2')}</strong>
      {translate('tours.welcome_tour.steps.company.content.3')}
    </Typography>

    <Typography
      variant="body1"
      component="span"
      sx={{ marginBottom: '14px', textAlign: 'left', display: 'block' }}>
      {translate('tours.welcome_tour.steps.company.footer')}
    </Typography>
  </>
);

const StepContent = ({ title, content }: { title: TranslationKeys; content: TranslationKeys }) => {
  const translate = useTranslate();

  return (
    <>
      <Typography
        variant="body1"
        component="span"
        sx={{ fontWeight: '800', textAlign: 'left', display: 'block' }}>
        {translate(title)}
      </Typography>

      <Typography variant="body1" component="span" sx={{ textAlign: 'left', display: 'block' }}>
        {translate(content)}
      </Typography>
    </>
  );
};

const trapFocus = () => {
  const handleTab = (event: KeyboardEvent) => {
    if (event.code.toLowerCase() !== 'tab') {
      return;
    }

    const tooltip = document.querySelector('#react-joyride-step-0 [role="alertdialog"]');

    if (!tooltip) {
      document.removeEventListener('keydown', handleTab);
      return;
    }

    const focusableElements = Array.from(tooltip.querySelectorAll('button')) as HTMLElement[];
    const [firstButton] = focusableElements;
    const lastButton = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === firstButton) {
        event.preventDefault();
        lastButton.focus();
      }
    } else if (document.activeElement === lastButton) {
      event.preventDefault();
      firstButton.focus();
    }
  };

  setTimeout(() => {
    document.addEventListener('keydown', handleTab);

    const primaryButton = document.querySelector<HTMLElement>(
      '#react-joyride-step-0 button[data-action="primary"]',
    );

    primaryButton?.focus();
  }, 300);
};

export const useGetWelcomeTour = ({ companyId }: { companyId: number | null }): TourType => {
  const translate = useTranslate();

  const canAccessCompaniesShow = useCanAccess({
    resource: 'companies',
    action: 'show',
  });
  const canAccessEmployeeCreate = useCanAccess({
    resource: 'employees',
    action: 'create',
  });
  const canAccessEmployeeBulkUpload = useCanAccess({
    resource: 'employees/bulk_create_validate',
    action: 'create',
  });
  const canAccessCollectiveOperations = useCanAccess({
    resource: 'collective_operations',
    action: 'list',
  });

  // redirect to the first accessible screen before the actual tour starts
  // to prevent target not found. The order mirrors the steps below so we
  // land on the page of the first step the user can actually access
  // (e.g. admin_level_1 has neither companies:show nor employees access).
  const firstScreen = useMemo(() => {
    if (G.isNotNullable(companyId) && canAccessCompaniesShow) {
      return buildLink('companies', 'show', companyId);
    } else if (canAccessEmployeeCreate || canAccessEmployeeBulkUpload) {
      return '/employees';
    } else if (canAccessCollectiveOperations) {
      return '/collective_operations';
    } else {
      return '/';
    }
  }, [
    companyId,
    canAccessCompaniesShow,
    canAccessEmployeeCreate,
    canAccessEmployeeBulkUpload,
    canAccessCollectiveOperations,
  ]);

  return {
    before: ({ redirect }) => {
      redirect(firstScreen);
    },
    steps: [
      // Company steps
      ...(G.isNotNullable(companyId) && canAccessCompaniesShow
        ? ([
            {
              target: `[data-tour-id='company-show-view-tabs'] .MuiTabs-root`,
              content: <CompanyStep translate={translate} />,
              disableBeacon: true,
              joyrideProps: {
                placement: 'right',
                disableScrollParentFix: true,
                disableScrolling: true,
                spotlightPadding: 0,
              },
              before: ({ redirect }) => {
                redirect(buildLink('companies', 'show', companyId));
                trapFocus();
              },
            },
          ] satisfies StepType[])
        : []),

      // Employees create step
      ...(canAccessEmployeeCreate
        ? ([
            {
              target: `[data-tour-id='employee-create-action-btn']`,
              content: (
                <StepContent
                  title={'tours.welcome_tour.steps.employees_create.title'}
                  content={'tours.welcome_tour.steps.employees_create.content'}
                />
              ),
              disableBeacon: true,
              before: ({ redirect }) => {
                redirect(buildLink('employees', 'list'));
              },
            },
          ] satisfies StepType[])
        : []),

      // Employees bulk upload step
      ...(canAccessEmployeeBulkUpload
        ? ([
            {
              target: `[data-tour-id='employee-bulk-upload-action-btn']`,
              content: (
                <StepContent
                  title={'tours.welcome_tour.steps.employees_bulk_upload.title'}
                  content={'tours.welcome_tour.steps.employees_bulk_upload.content'}
                />
              ),
              disableBeacon: true,
              before: ({ redirect }) => {
                redirect(buildLink('employees', 'list'));
              },
              after: ({ redirect }) => {
                redirect(buildLink('campaigns', 'list'));
              },
            },
          ] satisfies StepType[])
        : []),

      // Collective operations step
      ...(canAccessCollectiveOperations
        ? ([
            {
              target: '#main-content .list-page',
              content: (
                <StepContent
                  title={'tours.welcome_tour.steps.campaigns.title'}
                  content={'tours.welcome_tour.steps.campaigns.content'}
                />
              ),
              disableBeacon: true,
              before: ({ redirect }) => {
                redirect(buildLink('collective_operations', 'list'));
              },
            },
          ] satisfies StepType[])
        : []),

      // Final step
      {
        target: `[data-tour-id='profile-popup']`,
        content: (
          <StepContent
            title={'tours.welcome_tour.steps.final.title'}
            content={'tours.welcome_tour.steps.final.content'}
          />
        ),
        after: ({ setTutorialDone }): void => {
          setTutorialDone(true);
        },
        joyrideProps: {
          styles: {
            options: {
              ...lightOverlayOptions,
            },
          },
          spotlightPadding: 0,
        },
      },
    ],
  };
};
